var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable no-constant-condition */
import analytics from './analytics/analytics';
import { VoteType } from './chatTypes';
import broadcast, { BroadcastType } from './utils/pennyBroadcast';
import { updatePennyDebugger } from './utils/updatePennyDebugger';
import { browserStorage } from './utils/browserStorage';
import { localCache } from './localMessagsCache';
const CHAT_URL = 'https://qaa.priceline.com/genai-svc/genai/chat/st/travelChat';
const JSON_START_DELIMITER = 'jsonstart';
const JSON_END_DELIMITER = 'jsonend';
const CHUNK_DELIMITER = '\r\r\n';
/**
 * fetchChatStream
 * fetch module to handle stream response from openai api
 * @public
 */
export const fetchChatStream = ({
  submitChatMessagesPayload,
  url,
  // End Speech Controls
  headers,
  setStreamingText,
  handleJSON
}) => __awaiter(void 0, void 0, void 0, function* () {
  var _a, _b;
  const pennysid = (_a = browserStorage === null || browserStorage === void 0 ? void 0 : browserStorage.getItem('pennysid')) !== null && _a !== void 0 ? _a : '';
  const transformedSubmitChatMessagesPayload = Object.assign(Object.assign({}, submitChatMessagesPayload), {
    pennysid,
    prompts: [...localCache.getAssistantRefPrompts(), ...submitChatMessagesPayload.prompts]
  });
  try {
    let blob;
    const compressionEnabled = 'CompressionStream' in self;
    if (compressionEnabled) {
      const stream = new Blob([JSON.stringify(transformedSubmitChatMessagesPayload)], {
        type: 'application/json'
      }).stream();
      // This is delibrate and would be fixed later
      // @ts-ignore
      const compressedReadableStream = stream.pipeThrough(new CompressionStream('gzip'));
      const compressedResponse = yield new Response(compressedReadableStream);
      blob = yield compressedResponse.blob();
    }
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, 30000); // 30 seconds timeout for fetch request
    const res = yield fetch(url || CHAT_URL, {
      method: 'POST',
      body: compressionEnabled ? blob : JSON.stringify(transformedSubmitChatMessagesPayload),
      headers: Object.assign(Object.assign({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }, headers), {
        'Content-Encoding': compressionEnabled ? 'gzip' : 'identity'
      }),
      signal: abortController.signal
    });
    const timestamp = Date.now();
    if (submitChatMessagesPayload === null || submitChatMessagesPayload === void 0 ? void 0 : submitChatMessagesPayload.includeSpeech) {
      broadcast({
        type: BroadcastType.NEW_MESSAGE_REQUEST_INITIATED,
        messageId: timestamp,
        sender: 'submitChatMessages.fetchChatStream'
      });
    }
    clearTimeout(timeoutId);
    if (res.status !== 200) {
      throw Error(`${res.status}: ${res.statusText}`);
    }
    const pennysidHeader = res.headers.get('Pennysid');
    if (pennysidHeader) {
      browserStorage.setItem('pennysid', pennysidHeader);
    }
    if (res.body && res.body.getReader) {
      const reader = res.body.getReader();
      let hasJson = false;
      let finalJson = '';
      let timer;
      while (true) {
        try {
          const result = yield reader.read();
          if (result === null || result === void 0 ? void 0 : result.done) {
            if (timer) {
              clearTimeout(timer);
            }
            setStreamingText('STREAMING_ENDED');
            return {
              isOk: true
            };
          }
          if (result === null || result === void 0 ? void 0 : result.value) {
            if (timer) {
              clearTimeout(timer);
            }
            timer = setTimeout(() => {
              abortController.abort();
            }, 1000 * 20); // waiting for next chunk for 20s, if not received, abort request
            const resultValue = (_b = result.value) === null || _b === void 0 ? void 0 : _b.filter(x => x !== 0);
            const response = new Response(resultValue);
            const textRes = yield response.text();
            const textArray = textRes.split(CHUNK_DELIMITER);
            while (textArray.length > 0) {
              const text = textArray.shift();
              if (text === JSON_START_DELIMITER) {
                hasJson = true;
              } else if (text === JSON_END_DELIMITER) {
                hasJson = false;
                handleJSON(finalJson);
                finalJson = '';
              } else if (hasJson) {
                finalJson += text;
              } else {
                setStreamingText(text);
                if (submitChatMessagesPayload === null || submitChatMessagesPayload === void 0 ? void 0 : submitChatMessagesPayload.includeSpeech) {
                  broadcast({
                    type: BroadcastType.ADD_TEXT_TO_SPEECH_QUEUE,
                    data: text.replace(/\0/g, ''),
                    messageId: timestamp,
                    sender: 'submitChatMessages.fetchChatStream'
                  });
                }
              }
            }
          }
        } catch (err) {
          return {
            isOk: false,
            errorCode: 'READER_READ_ERROR',
            errorMessage: err === null || err === void 0 ? void 0 : err.message
          };
        }
      }
    } else {
      return {
        isOk: false,
        errorCode: 'NOT_SUPPORTED',
        errorMessage: 'ReadableStream not supported'
      };
    }
  } catch (err) {
    setStreamingText('STREAMING_ENDED');
    return {
      isOk: false,
      errorCode: 'ERROR',
      errorMessage: err === null || err === void 0 ? void 0 : err.message
    };
  }
});
/**
 * submitChatMessages
 * post give message
 * @public
 */
export const submitChatMessages = ({
  chatMessages,
  handleMarkerResponse,
  markerComponents,
  requestOptions: {
    url = '',
    headers = {}
  },
  sentenceArrayBuilder,
  setChatMessages: setChatMessages,
  setErrorMessage,
  setIsDisablingInputValue,
  setLoadingStatus,
  setLoadingStatusMessage,
  setOpenErrorComponent,
  setStreamingText,
  speechFetcher,
  submitChatMessagesPayload
}) => __awaiter(void 0, void 0, void 0, function* () {
  var _c;
  setLoadingStatus(true); // display loading
  setErrorMessage === null || setErrorMessage === void 0 ? void 0 : setErrorMessage(''); // remove error message
  const fullTexts = [];
  const JSONResponses = [];
  const statusMessages = [];
  let containInlineMetadata = false;
  let delayFinalResponseText = false;
  analytics.log({
    message: 'Submit Chat Messages started'
  });
  const result = yield fetchChatStream({
    submitChatMessagesPayload,
    url,
    speechFetcher,
    sentenceArrayBuilder,
    headers,
    handleJSON: jsonstring => {
      var _a;
      const parsedJson = JSON.parse(jsonstring);
      if (!containInlineMetadata && parsedJson.displayComponentName === 'INLINE_METADATA_MARKER') {
        containInlineMetadata = true;
      }
      if (!delayFinalResponseText && parsedJson.displayComponentName === 'HOTEL_CARDS_MARKER') {
        delayFinalResponseText = true;
      }
      if (setLoadingStatusMessage && parsedJson.displayComponentName === 'LOADING_STATUS_MESSAGE_MARKER') {
        statusMessages.push((_a = parsedJson.componentProps) === null || _a === void 0 ? void 0 : _a.statusMessage);
        setLoadingStatusMessage(statusMessages);
      } else {
        JSONResponses.push(parsedJson);
      }
    },
    setStreamingText: text => {
      if (text !== 'STREAMING_ENDED') {
        fullTexts.push(text);
        const fullText = fullTexts.join('');
        // Display streaming text
        setStreamingText === null || setStreamingText === void 0 ? void 0 : setStreamingText(fullText);
      } else {
        // Clear live streaming text, when streaming ends
        setStreamingText === null || setStreamingText === void 0 ? void 0 : setStreamingText('');
      }
    }
  });
  // display the final full texts
  if ((result === null || result === void 0 ? void 0 : result.isOk) && fullTexts.length !== 0) {
    const date = new Date();
    const chatMsgs = [];
    const finalTexts = fullTexts.join('').trim();
    if (finalTexts.toLowerCase().startsWith('system error')) {
      setIsDisablingInputValue === null || setIsDisablingInputValue === void 0 ? void 0 : setIsDisablingInputValue(true);
    }
    if (finalTexts.length) {
      const promptMsg = {
        role: 'assistant',
        content: finalTexts
      };
      // When there is INLINE_METADATA_MARKER in the response
      // DON'T display the finalTexts, INLINE_METADATA_MARKER component will parse the metadata and display the fullTexts with inline metadata.
      if (!containInlineMetadata && !delayFinalResponseText) {
        const chatMsg = Object.assign(Object.assign({}, promptMsg), {
          date,
          vote: VoteType.NONE
        });
        chatMsgs.push(chatMsg);
      }
    }
    // Display the Marker component
    if (JSONResponses.length > 0) {
      JSONResponses.forEach(jsonResponse => {
        const {
          displayComponentName,
          content: textContent,
          componentProps,
          assistantPromptMessage
        } = jsonResponse;
        if (typeof handleMarkerResponse === 'function' && (markerComponents === null || markerComponents === void 0 ? void 0 : markerComponents[displayComponentName])) {
          const {
            role = 'assistant',
            content = displayComponentName,
            messageContent = '',
            markerComponentProps = {}
          } = handleMarkerResponse(undefined, displayComponentName, textContent, componentProps, chatMessages);
          const date = new Date();
          // Text content Message
          if (messageContent || content && content !== displayComponentName) {
            const chatMessage = {
              role,
              content: messageContent || content,
              date,
              vote: VoteType.NONE
            };
            // Display Text content Message
            chatMsgs.push(chatMessage);
          }
          // Marker component Message
          const chatMessage = {
            role,
            content: '',
            displayComponentName,
            markerComponentProps: Object.assign(Object.assign({}, componentProps), markerComponentProps),
            date,
            vote: VoteType.NONE
          };
          // Display Marker component Message
          chatMsgs.push(chatMessage);
        } else {
          const date = new Date();
          // Default Mark component message, like QUICK_ACTIONS
          const componentMessage = {
            role: '',
            content: '',
            displayComponentName,
            markerComponentProps: Object.assign({}, componentProps),
            date,
            vote: VoteType.NONE
          };
          // Display default Mark component message, like QUICK_ACTIONS
          chatMsgs.push(componentMessage);
          // Display text message along with the Mark component
          const shouldDisplayDelayFinalText = delayFinalResponseText && finalTexts.length > 0 && !containInlineMetadata;
          const shouldDisplayMarkerTextContent = textContent && textContent !== displayComponentName;
          if (shouldDisplayMarkerTextContent || shouldDisplayDelayFinalText) {
            const chatMessage = {
              role: 'assistant',
              content: '',
              date,
              vote: VoteType.NONE
            };
            if (shouldDisplayDelayFinalText) {
              delayFinalResponseText = false;
              chatMessage.content = finalTexts;
              // Display Text content Message
              chatMsgs.push(chatMessage);
            }
            if (shouldDisplayMarkerTextContent) {
              chatMessage.content = textContent;
              // Display Text content Message
              chatMsgs.push(chatMessage);
            }
          }
        }
        // Save functions response to prompts, so Penny can reference it later
        // This message doesn't display to the user
        if (assistantPromptMessage) {
          localCache.setAssistantRefPrompts(assistantPromptMessage);
        }
      });
    }
    updatePennyDebugger(submitChatMessagesPayload, [...chatMessages, ...chatMsgs], headers);
    setChatMessages([...chatMessages, ...chatMsgs]);
    setLoadingStatus(false);
    setLoadingStatusMessage([]);
  } else if (!(result === null || result === void 0 ? void 0 : result.isOk)) {
    if (typeof handleMarkerResponse === 'function') {
      handleMarkerResponse(result, '');
    }
    analytics.log(Object.assign({
      message: 'Failed to submit'
    }, result));
    setErrorMessage === null || setErrorMessage === void 0 ? void 0 : setErrorMessage((_c = result.errorMessage) !== null && _c !== void 0 ? _c : 'Network error! Please check your network connection and retry.');
    setOpenErrorComponent(true);
    setLoadingStatus(false);
    setLoadingStatusMessage([]);
  }
});